import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Star,
  User,
  ThumbsUp,
  Share,
  Camera,
  MapPin,
  Award,
  TrendingUp,
  Users,
  MessageSquare
} from 'lucide-react';
import { reviews } from '../mock';

const Reviews = () => {
  const [showAll, setShowAll] = useState(false);
  const displayedReviews = showAll ? reviews : reviews.slice(0, 6);

  const averageRating = 4.8;
  const totalReviews = reviews.length;
  
  const ratingDistribution = [
    { stars: 5, count: 12, percentage: 85.7 },
    { stars: 4, count: 2, percentage: 14.3 },
    { stars: 3, count: 0, percentage: 0 },
    { stars: 2, count: 0, percentage: 0 },
    { stars: 1, count: 0, percentage: 0 }
  ];

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`h-4 w-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                Avis <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">Clients</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Découvrez ce que nos clients disent de leur expérience à la Pharmacie Meyrin-Vergers. 
                Leurs témoignages reflètent notre engagement envers l'excellence.
              </p>
            </div>

            {/* Rating Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8 items-center">
                <div className="text-center">
                  <div className="text-6xl font-bold text-teal-600 mb-2">{averageRating}</div>
                  <div className="flex justify-center mb-2">
                    {renderStars(5)}
                  </div>
                  <div className="text-gray-600">{totalReviews} avis clients</div>
                </div>

                <div className="space-y-2">
                  {ratingDistribution.reverse().map((item) => (
                    <div key={item.stars} className="flex items-center space-x-2">
                      <span className="text-sm w-8">{item.stars}★</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-teal-500 to-emerald-500 h-2 rounded-full" 
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600 w-12">{item.count}</span>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-teal-50 rounded-xl p-4 text-center">
                    <TrendingUp className="h-6 w-6 text-teal-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">98%</div>
                    <div className="text-xs text-gray-600">Recommandations</div>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <Users className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">1000+</div>
                    <div className="text-xs text-gray-600">Clients servis</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedReviews.map((review) => (
              <Card key={review.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full p-2 flex items-center justify-center">
                        <User className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900 text-sm">
                          {review.name}
                        </h3>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          {review.guide && (
                            <div className="flex items-center space-x-1">
                              <MapPin className="h-3 w-3" />
                              <span>Guide Local</span>
                            </div>
                          )}
                          {review.reviews && (
                            <span>• {review.reviews} avis</span>
                          )}
                          {review.photos && (
                            <div className="flex items-center space-x-1">
                              <Camera className="h-3 w-3" />
                              <span>{review.photos}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{review.date}</div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center space-x-1 mb-3">
                    {renderStars(review.rating)}
                  </div>

                  {/* Comment */}
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">
                    "{review.comment}"
                  </p>

                  {/* Actions */}
                  <div className="flex items-center justify-between text-xs">
                    <button className="flex items-center space-x-1 text-gray-500 hover:text-teal-600 transition-colors">
                      <ThumbsUp className="h-3 w-3" />
                      <span>Utile</span>
                    </button>
                    <button className="flex items-center space-x-1 text-gray-500 hover:text-teal-600 transition-colors">
                      <Share className="h-3 w-3" />
                      <span>Partager</span>
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {!showAll && reviews.length > 6 && (
            <div className="text-center mt-12">
              <Button 
                onClick={() => setShowAll(true)}
                variant="outline" 
                size="lg" 
                className="border-teal-600 text-teal-600 hover:bg-teal-50"
              >
                <MessageSquare className="h-5 w-5 mr-2" />
                Voir Tous les Avis ({reviews.length - 6} de plus)
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Points Forts Selon Nos Clients
            </h2>
            <p className="text-xl text-gray-600">
              Ce qui fait la différence de notre pharmacie
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white border-0 shadow-lg text-center">
              <CardContent className="p-6">
                <Award className="h-8 w-8 text-yellow-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Professionnalisme</h3>
                <p className="text-sm text-gray-600">Personnel qualifié et attentif</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-lg text-center">
              <CardContent className="p-6">
                <MapPin className="h-8 w-8 text-teal-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Emplacement Idéal</h3>
                <p className="text-sm text-gray-600">Face à l'hôpital, facile d'accès</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-lg text-center">
              <CardContent className="p-6">
                <Star className="h-8 w-8 text-purple-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Pharmacie Moderne</h3>
                <p className="text-sm text-gray-600">Équipements et locaux récents</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-lg text-center">
              <CardContent className="p-6">
                <Users className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Service Client</h3>
                <p className="text-sm text-gray-600">Accueil chaleureux et conseils</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-6">
              Rejoignez Nos Clients Satisfaits
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              Découvrez par vous-même pourquoi nos clients nous recommandent. 
              Venez nous rendre visite dans notre pharmacie moderne de Meyrin-Vergers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="bg-white text-teal-600 hover:bg-gray-50 px-8 py-4">
                <MapPin className="h-5 w-5 mr-2" />
                Nous Trouver
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-teal-600 px-8 py-4">
                <MessageSquare className="h-5 w-5 mr-2" />
                Laisser un Avis
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;